import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { DashboardLayout } from '../components';
import { DocumentPreviewModal, useDocumentPreview } from '../components/DocumentPreviewModal';
import { Notice } from '../components/Notice';
import { useAuth } from '../context/AuthContext';
import { documentApi } from '../services/api';
import type { DocumentRecord } from '../types';
import { projectBasePath } from '../utils/notificationLinks';

/**
 * A file linked from an email (ClickUp 869fmfeah): `/files/:documentId`.
 * Behind login, so the reader returns here after signing in. The document is
 * read through the same authenticated documents API as Dokument and opens in
 * the shared preview; a missing file or one the reader cannot see gives a
 * friendly error page instead.
 */
export function FileView() {
  const { t } = useTranslation();
  const { documentId = '' } = useParams();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [file, setFile] = useState<DocumentRecord | null>(null);
  const [status, setStatus] = useState<'loading' | 'ready' | 'error'>('loading');
  const preview = useDocumentPreview();
  const { openPreview } = preview;

  useEffect(() => {
    let active = true;
    setStatus('loading');
    documentApi.getDocumentDetails(documentId)
      .then((record) => {
        if (!active) return;
        setFile(record);
        setStatus('ready');
        void openPreview(record);
      })
      .catch(() => {
        if (active) setStatus('error');
      });
    return () => {
      active = false;
    };
    // openPreview is recreated each render; the file is loaded once per id.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [documentId]);

  const projectDocumentsPath = file?.projectId
    ? `${projectBasePath(user)}/${file.projectId}?tab=documents`
    : null;

  return (
    <DashboardLayout>
      <div className="file-view" data-testid="file-view">
        {status === 'loading' && (
          <div className="d-flex justify-content-center align-items-center py-5">
            <div className="spinner-border text-primary" role="status">
              <span className="visually-hidden">{t('common.loading')}</span>
            </div>
          </div>
        )}

        {status === 'error' && (
          <Notice
            level="warning"
            testId="file-view-error"
            title={t('fileView.errorTitle')}
            action={{ label: t('fileView.goHome'), onClick: () => navigate('/home') }}
          >
            {t('fileView.errorText')}
          </Notice>
        )}

        {status === 'ready' && file && (
          <div className="card">
            <div className="card-body">
              <p className="text-muted small mb-1">{t('fileView.label')}</p>
              <h1 className="h5 fw-bold text-break mb-3">{file.originalName}</h1>
              <div className="d-flex flex-wrap gap-2">
                <button
                  type="button"
                  className="btn btn-primary"
                  onClick={() => void openPreview(file)}
                >
                  <i className="feather-eye me-1" aria-hidden="true"></i>
                  {t('fileView.preview')}
                </button>
                <button
                  type="button"
                  className="btn btn-outline-primary"
                  onClick={() => void documentApi.downloadFile(file.id, file.originalName)}
                >
                  <i className="feather-download me-1" aria-hidden="true"></i>
                  {t('documents.preview.download')}
                </button>
                {projectDocumentsPath && (
                  <Link className="btn btn-outline-secondary" to={projectDocumentsPath}>
                    {t('fileView.openInProject')}
                  </Link>
                )}
              </div>
            </div>
          </div>
        )}
      </div>

      <DocumentPreviewModal
        previewDoc={preview.previewDoc}
        previewUrl={preview.previewUrl}
        previewHtml={preview.previewHtml}
        previewLoading={preview.previewLoading}
        previewError={preview.previewError}
        previewErrorText={preview.previewErrorText}
        onClose={preview.closePreview}
      />
    </DashboardLayout>
  );
}

export default FileView;
